import { useState, useEffect, useCallback, useRef } from "react";
import { api, ApiError } from "./api.js";

// Small data-loading hook for the wizard's server-backed resources
// (listings / escrows / bbas). Returns { data, loading, error, reload }.
// `enabled` defers the fetch until the agent actually reaches that step.
//
// error is shaped { message, code, status } so the UI can distinguish a
// not-connected upstream (e.g. spark_not_connected → "connect ARMLS") from a
// transient failure (offer a retry).
export function useResource(path, { enabled = true, pick } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const reqId = useRef(0);

  // Keep `pick` in a ref so an inline callback (new identity each render) does
  // NOT recreate `reload` and refire the effect in a loop. `reload` stays stable
  // for a given `path`.
  const pickRef = useRef(pick);
  pickRef.current = pick;

  const reload = useCallback(async (opts = {}) => {
    const id = ++reqId.current;
    setLoading(true);
    setError(null);
    try {
      const qs = opts.live ? (path.includes("?") ? "&live=1" : "?live=1") : "";
      const res = await api.get(path + qs);
      if (id !== reqId.current) return; // a newer request superseded this one
      setData(pickRef.current ? pickRef.current(res) : res);
    } catch (err) {
      if (id !== reqId.current) return;
      setError({
        message: err.message || "Failed to load",
        code: err instanceof ApiError ? err.code : undefined,
        status: err instanceof ApiError ? err.status : undefined,
      });
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [path]);

  useEffect(() => {
    if (enabled) reload();
  }, [enabled, reload]);

  return { data, loading, error, reload };
}
